import { useState, useEffect } from "react";
import { THEMES, RARITY, TAROT_CARDS } from "../data/tarotCards";
import { getDailyCard, saveCardToCollection, personalizeMessage, getUserCollection, todayStr } from "../utils/dailyCard";
import { btn3d } from "../styles/buttonStyles";

/* ─── 작은 위젯 (홈 화면 상단) ─── */
export function DailyCardWidget({ uid, mbti, zodiac, name, darkMode = false, onOpen }) {
  const dm = darkMode;
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    // 오늘 이미 뽑았는지 체크
    try {
      const raw = localStorage.getItem("tripcanvas_daily_cards") || "{}";
      const data = JSON.parse(raw);
      const key = `${uid || "guest"}_${todayStr()}`;
      setDrawn(!!data[key]);
    } catch (e) {}
  }, [uid]);

  return (
    <div
      onClick={onOpen}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen && onOpen();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={drawn ? "오늘의 여행 카드 다시 보기" : "오늘의 여행 카드 뽑기"}
      className="daily-card-widget"
      style={{
        cursor: "pointer",
        padding: "12px 14px",
        borderRadius: 14,
        background: dm
          ? "linear-gradient(135deg, rgba(91,108,219,0.18), rgba(155,141,255,0.12))"
          : "linear-gradient(135deg, rgba(91,108,219,0.10), rgba(129,140,248,0.08))",
        border: `1px solid ${dm ? "rgba(155,141,255,0.35)" : "rgba(91,108,219,0.25)"}`,
        marginBottom: 14,
        display: "flex",
        alignItems: "center",
        gap: 12,
        transition: "transform 0.12s, box-shadow 0.22s",
        boxShadow: dm ? "0 4px 16px rgba(91,108,219,0.2)" : "0 3px 12px rgba(91,108,219,0.12)",
      }}
    >
      {/* 카드 미니어처 */}
      <div style={{
        width: 38, height: 50, borderRadius: 6,
        background: drawn
          ? "linear-gradient(135deg, #5B6CDB, #9B8DFF)"
          : `linear-gradient(135deg, ${dm ? "#3D2570" : "#9B72CF"}, ${dm ? "#1E1E50" : "#5B6CDB"})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, color: "#fff",
        boxShadow: drawn ? "0 2px 8px rgba(91,108,219,0.4)" : "0 2px 8px rgba(0,0,0,0.2)",
        flexShrink: 0,
        position: "relative",
      }}>
        {drawn ? "✨" : "🎴"}
        {!drawn && (
          <div style={{
            position: "absolute", top: -4, right: -4,
            width: 10, height: 10, borderRadius: "50%",
            background: "#FF5050",
            border: "2px solid #fff",
          }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: dm ? "#C890FF" : "#5B6CDB", letterSpacing: "0.06em" }}>
          {drawn ? "✦ 오늘의 카드 확인" : "✨ 오늘의 여행 카드"}
        </div>
        <div style={{ fontSize: 10, color: dm ? "rgba(226,232,240,0.68)" : "rgba(51,65,85,0.62)", marginTop: 2 }}>
          {drawn ? "다시 보고 공유하기" : "탭해서 오늘의 운명을 뽑아보세요"}
        </div>
      </div>
      <div style={{ fontSize: 18, color: dm ? "#9B8DFF" : "#5B6CDB" }}>›</div>
    </div>
  );
}

/* ─── 풀 화면 카드 모달 ─── */
export function DailyCardModal({ uid, mbti, zodiac, name, darkMode = false, onClose, onTravel, onCollection }) {
  const dm = darkMode;
  const [card, setCard] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [shareMsg, setShareMsg] = useState("");

  useEffect(() => {
    const c = getDailyCard({ uid, mbti, zodiac });
    setCard(c);
    saveCardToCollection(uid || "guest", c);
    // 0.5초 후 카드 뒤집기
    setTimeout(() => setRevealed(true), 600);
  }, [uid, mbti, zodiac]);

  if (!card) return null;
  const theme = THEMES[card.theme] || THEMES.earth;
  const rarity = RARITY[card.rarity] || RARITY.common;
  const today = new Date().toLocaleDateString("ko-KR", { month: "numeric", day: "numeric", weekday: "short" });

  const handleShare = async () => {
    const text = `🎴 오늘의 여행 카드\n\n[${card.name}]\n${personalizeMessage(card.message, name)}\n\n💫 추천: ${card.destinations.slice(0,2).join(", ")}\n🕐 ${card.luckyTime}\n\n#TripCanvas #오늘의여행카드`;
    try {
      await navigator.clipboard.writeText(text);
      setShareMsg("✓ 카드 내용이 복사됐어요");
    } catch (e) {
      setShareMsg("복사 실패 — 길게 눌러 복사하세요");
    }
    setTimeout(() => setShareMsg(""), 2500);
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}>
      <div style={{ width: "100%", maxWidth: 360, position: "relative" }}>
        {/* 닫기 버튼 */}
        <button type="button" onClick={onClose} aria-label="오늘의 여행 카드 닫기" className="btn3d"
          style={{
            ...btn3d("outline", dm),
            position: "absolute", top: -10, right: -4, zIndex: 10,
            width: 36, height: 36, borderRadius: "50%",
            padding: 0, fontSize: 18,
          }}>✕</button>

        {/* 카드 본체 */}
        <div style={{
          aspectRatio: "3 / 5", borderRadius: 22, padding: "22px 20px",
          background: `linear-gradient(155deg, ${theme.colors[0]}, ${theme.colors[1]})`,
          color: "#fff", display: "flex", flexDirection: "column",
          boxShadow: `0 20px 60px ${rarity.glow}, 0 0 30px ${rarity.glow}`,
          border: `2px solid rgba(255,255,255,0.25)`,
          position: "relative", overflow: "hidden",
          transform: revealed ? "rotateY(0)" : "rotateY(180deg)",
          transition: "transform 0.7s cubic-bezier(0.5,0,0.3,1)",
          transformStyle: "preserve-3d",
        }}>
          {/* 백그라운드 패턴 */}
          <div style={{
            position: "absolute", inset: 0, opacity: 0.08, pointerEvents: "none",
            backgroundImage: `radial-gradient(circle at 20% 20%, white 1px, transparent 1.5px), radial-gradient(circle at 80% 60%, white 1px, transparent 1.5px), radial-gradient(circle at 50% 80%, white 1px, transparent 1.5px)`,
            backgroundSize: "60px 60px, 80px 80px, 100px 100px",
          }} />

          {/* 상단: 테마 + 날짜 + 등급 */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div style={{
              padding: "3px 10px", borderRadius: 100,
              background: "rgba(255,255,255,0.22)",
              fontSize: 9, fontWeight: 800, letterSpacing: "0.18em",
            }}>
              {theme.symbol} {theme.name}
            </div>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 600 }}>{today}</div>
          </div>

          {/* 등급 라벨 */}
          {card.rarity !== "common" && (
            <div style={{
              alignSelf: "center", marginBottom: 4,
              padding: "2px 12px", borderRadius: 100,
              background: rarity.color, color: "#fff",
              fontSize: 9, fontWeight: 800, letterSpacing: "0.15em",
              boxShadow: `0 0 12px ${rarity.glow}`,
            }}>
              ✦ {rarity.ko.toUpperCase()} ✦
            </div>
          )}

          {/* 가운데: 큰 이모지 + 카드 이름 */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "14px 0" }}>
            <div style={{
              fontSize: 80, lineHeight: 1, filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
            }}>
              {card.emoji}
            </div>
            <div style={{
                            fontSize: 20, fontWeight: 700, textAlign: "center",
              letterSpacing: "0.02em", textShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}>
              {card.name}
            </div>
          </div>

          {/* 메시지 */}
          <div style={{
            background: "rgba(255,255,255,0.13)", borderRadius: 12,
            padding: "12px 14px", marginBottom: 10,
            fontSize: 12, lineHeight: 1.7, fontWeight: 500,
            backdropFilter: "blur(4px)",
          }}>
            {personalizeMessage(card.message, name)}
          </div>

          {/* 하단 정보 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 10.5, fontWeight: 600 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.85 }}>💫 추천</span>
              <span style={{ fontWeight: 800 }}>{card.destinations.slice(0, 2).join(" · ")}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.85 }}>🕐 행운의 시간</span>
              <span style={{ fontWeight: 800 }}>{card.luckyTime}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.85 }}>🔢 행운의 숫자</span>
              <span style={{ fontWeight: 800 }}>{card.luckyNumber}</span>
            </div>
          </div>

          {/* 워터마크 */}
          <div style={{
            marginTop: 10, paddingTop: 8,
            borderTop: "1px solid rgba(255,255,255,0.18)",
            textAlign: "center", fontSize: 9, opacity: 0.65, letterSpacing: "0.15em",
          }}>
            ✦ TRIPCANVAS ✦
          </div>
        </div>

        {/* 액션 버튼 */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button type="button" onClick={handleShare} className="btn3d"
            style={{
              ...btn3d("white", dm),
              flex: 1, padding: "11px 6px", minHeight: 44, borderRadius: 11,
              fontSize: 12, fontWeight: 800,
            }}>
            📤 공유
          </button>
          <button type="button" onClick={() => onTravel && onTravel(card.destinations[0])} className="btn3d"
            style={{
              ...btn3d("primary", dm),
              flex: 1.4, padding: "11px 6px", minHeight: 44, borderRadius: 11,
              fontSize: 12, fontWeight: 800,
            }}>
            🗺️ 여기로 떠나기
          </button>
          <button type="button" onClick={onCollection} className="btn3d"
            style={{
              ...btn3d("outline", dm),
              flex: 1, padding: "11px 6px", minHeight: 44, borderRadius: 11,
              fontSize: 12, fontWeight: 800,
            }}>
            📚 도감
          </button>
        </div>

        {shareMsg && (
          <p style={{ marginTop: 10, fontSize: 12, color: "#fff", textAlign: "center", fontWeight: 700 }}>
            {shareMsg}
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── 컬렉션 (도감) ─── */
export function CardCollection({ uid, darkMode = false, onClose }) {
  const dm = darkMode;
  const [collection, setCollection] = useState([]);

  useEffect(() => {
    setCollection(getUserCollection(uid || "guest"));
  }, [uid]);

  const owned = new Set(collection.map(c => c.card.id));
  const total = TAROT_CARDS.length;
  const got = collection.length;
  const pct = Math.round((got / total) * 100);

  // 등급별 카운트
  const rarityCount = collection.reduce((acc, { card }) => {
    acc[card.rarity] = (acc[card.rarity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: 20, overflowY: "auto",
      }}>
      <div style={{ width: "100%", maxWidth: 480, position: "relative", paddingTop: 10 }}>
        {/* 헤더 */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h2 style={{
                            fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "0.04em",
            }}>📚 여행 카드 도감</h2>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
              내가 모은 카드 컬렉션
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="여행 카드 도감 닫기" className="btn3d"
            style={{
              ...btn3d("outline", dm),
              width: 36, height: 36, borderRadius: "50%",
              padding: 0, fontSize: 18,
            }}>✕</button>
        </div>

        {/* 진행 정보 */}
        <div style={{
          padding: "16px", borderRadius: 16, marginBottom: 18,
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 900, color: "#FFD060" }}>{got}</span>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>/ {total}장</span>
            <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 800, color: "#9B8DFF" }}>{pct}%</span>
          </div>
          <div style={{ height: 6, background: "rgba(255,255,255,0.12)", borderRadius: 99 }}>
            <div style={{
              width: `${pct}%`, height: "100%", borderRadius: 99,
              background: "linear-gradient(90deg, #F5A623, #9B8DFF)",
              transition: "width 0.6s",
            }} />
          </div>
          {/* 등급별 카운트 */}
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {Object.entries(RARITY).map(([key, r]) => (
              <div key={key} style={{
                fontSize: 10, padding: "3px 9px", borderRadius: 100,
                background: `${r.color}33`, border: `1px solid ${r.color}`,
                color: r.color, fontWeight: 800, letterSpacing: "0.05em",
              }}>
                {r.ko} {rarityCount[key] || 0}
              </div>
            ))}
          </div>
        </div>

        {/* 카드 그리드 (3열) */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8,
        }}>
          {TAROT_CARDS.map(c => {
            const got = owned.has(c.id);
            const theme = THEMES[c.theme];
            const rarity = RARITY[c.rarity];
            return (
              <div key={c.id}
                title={got ? c.name : "미획득"}
                style={{
                  aspectRatio: "3/4.5", borderRadius: 10, padding: "10px 6px",
                  background: got
                    ? `linear-gradient(155deg, ${theme.colors[0]}, ${theme.colors[1]})`
                    : "rgba(255,255,255,0.05)",
                  border: got ? `1.5px solid rgba(255,255,255,0.3)` : "1px dashed rgba(255,255,255,0.15)",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between",
                  filter: got ? "none" : "grayscale(1)",
                  opacity: got ? 1 : 0.4,
                  position: "relative", overflow: "hidden",
                  boxShadow: got ? `0 4px 12px ${rarity.glow}` : "none",
                }}>
                {got ? (
                  <>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.85)", fontWeight: 800, letterSpacing: "0.1em" }}>
                      {theme.symbol}
                    </div>
                    <div style={{ fontSize: 32, lineHeight: 1, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}>
                      {c.emoji}
                    </div>
                    <div style={{
                      fontSize: 8.5, fontWeight: 800, color: "#fff", textAlign: "center",
                      lineHeight: 1.25, padding: "0 2px",
                      fontFamily: "'Noto Serif KR',serif",
                    }}>
                      {c.name}
                    </div>
                  </>
                ) : (
                  <div style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 24, color: "rgba(255,255,255,0.3)",
                  }}>?</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
